import { useEffect, useState } from "react";

import { Button, DeleteModal, SortDropdownValue } from "@/modules/common/components";
import { useFilters } from "@/modules/common/filters";
import { AdminManagementCompanySortField, ManagementCompanyStatus } from "@/modules/common/types/ManagementCompanies.enum";
import { ROUTES } from "@/routes/constants/routes";
import { PlusIcon } from "lucide-react";
import useManagerTotalizers from "./hooks/useManagerTotalizers";
import useManagerList from "./hooks/useManagerList";
import ManagerTotalizers from "./components/ManagerTotalizers";
import ManagerFilterBar from "./components/ManagerFilterBar";
import ManagersList from "./components/ManagersList";
import useInactivateManagementCompany from "./hooks/useInactivateManagementCompany";
import {
  INACTIVATE_COMPANY_CONFIRM_TEXT,
  INACTIVATE_COMPANY_WARNING,
  buildInactivateCompanyDescription,
} from "./constants/InactivateManagementCompany";
import { MANAGEMENT_COMPANY_FILTER_FIELDS } from "./constants/ManagementCompanyFilterFields";
import { ListAdminManagementCompaniesDto } from "@/service/dto/admin-management-companies";
import { FILTER_KEYS } from "@/modules/common/filters/keys";
import { usePagination } from "@/modules/common/hooks/usePagination";
import { useSessionStore } from "@/modules/auth/store/useSessionStore";
import { AdminUserRole } from "@/modules/common/types/Users.enum";
import { AdminManagementCompanyResponse } from "@/service/response/admin-management-companies";

const Manager = () => {
  const { canUserSee } = useSessionStore();

  const { data: totalizers, isLoading: isTotalizersLoading } = useManagerTotalizers();

  const [sort, setSort] = useState<SortDropdownValue<AdminManagementCompanySortField> | null>(null);

  const filters = useFilters<ListAdminManagementCompaniesDto>({
    fields: MANAGEMENT_COMPANY_FILTER_FIELDS,
    filterKey: FILTER_KEYS.MANAGEMENT_COMPANIES,
    defaultValues: { status: [ManagementCompanyStatus.ACTIVE, ManagementCompanyStatus.PROVISIONING] },
  });

  const { page, setPage, limit, setPageSize } = usePagination();

  const [companyToInactivate, setCompanyToInactivate] = useState<AdminManagementCompanyResponse | null>(null);
  const inactivateManagementCompany = useInactivateManagementCompany();

  const handleConfirmInactivation = () => {
    if (!companyToInactivate) return;

    inactivateManagementCompany.mutate(companyToInactivate.id, {
      onSuccess: () => setCompanyToInactivate(null),
    });
  };

  const filtersKey = JSON.stringify(filters.queryParams);
  const sortKey = sort ? `${sort.field}:${sort.order}` : "";
  useEffect(() => {
    setPage(1);
  }, [filtersKey, sortKey]);

  const {
    data: managers,
    isLoading: isManagersLoading,
    isFetching: isManagersFetching,
  } = useManagerList({
    ...filters.queryParams,
    sortBy: sort?.field,
    sortOrder: sort?.order,
    page,
    limit,
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col">
          <p className="text-[22px] font-bold text-ui-primary">Gestoras</p>
          <p className="text-[14px] text-ui-text-muted">
            Cadastros de gestoras vinculadas à POUPenergia (Gestão de Terceiros ou Gestão Própria).
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          {canUserSee(AdminUserRole.ADMIN) && (
            <Button label="Cadastrar Gestora" route={ROUTES.MANAGER.ADD} icon={<PlusIcon />} className="w-full sm:w-auto" />
          )}
        </div>
      </div>

      <ManagerTotalizers data={totalizers} isLoading={isTotalizersLoading} />

      <ManagerFilterBar filters={filters} sort={sort} onSortChange={setSort} />

      <ManagersList
        data={managers}
        isLoading={isManagersLoading}
        isFetching={isManagersFetching}
        limit={limit}
        onChangePage={setPage}
        onChangePageSize={setPageSize}
        onInactivate={setCompanyToInactivate}
      />

      <DeleteModal
        open={!!companyToInactivate}
        title="Inativar gestora de demonstração"
        description={buildInactivateCompanyDescription(companyToInactivate?.name)}
        warning={INACTIVATE_COMPANY_WARNING}
        confirmText={INACTIVATE_COMPANY_CONFIRM_TEXT}
        loading={inactivateManagementCompany.isPending}
        onDelete={handleConfirmInactivation}
        onClose={() => setCompanyToInactivate(null)}
      />
    </div>
  );
};

export default Manager;
